/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import {
  ErrorBox,
  Flexbox,
  Form,
  LoadManager,
} from '@/components'
import useClinicalCare from '@/hooks/useClinicalCare'
import usePageTitle from '@/hooks/usePageTitle'
import yup from '@/lib/yupPt'
import { setClinicalCare } from '@/reducers/clinicalCare'
import clinicalCares from '@/services/api/clinicalCares'
import React, {
  useCallback, useEffect, useLayoutEffect, useState,
} from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'

const schema = yup.object().shape({
  clinic: yup
    .object()
    .shape({
      id: yup.number().required(),
    })
    .required(),
  scheduledTo: yup.date().required(),
  outsourcedTransport: yup.boolean(),
})

function Triage({ title }) {
  usePageTitle({ title })
  const [loading, setLoading] = useState(false)
  const [fetchingLicensings, setFetchingLicensings] = useState(false)
  const [licensings, setLicensings] = useState([])
  const { id } = useParams()
  const {
    state: { clinicalCare },
    dispatch,
  } = useClinicalCare()
  const [address, setAddress] = useState()
  const history = useHistory()

  useEffect(() => {
    if (clinicalCare?.address || clinicalCare?.clinic?.address) {
      // eslint-disable-next-line no-unused-vars, no-shadow
      const { id, ...rest } = clinicalCare?.address || clinicalCare?.clinic?.address
      setAddress(rest)
    }
  }, [clinicalCare?.address, clinicalCare?.clinic?.address])

  const fetchLicensings = useCallback(async () => {
    try {
      setFetchingLicensings(true)
      const { data } = await clinicalCares.allowedClinics()
      setLicensings(data)
    } finally {
      setFetchingLicensings(false)
    }
  }, [])

  useLayoutEffect(() => {
    fetchLicensings()
  }, [fetchLicensings])

  async function onSubmit(values) {
    try {
      setLoading(true)
      const eventData = {
        scheduledTo: values?.scheduledTo,
        description:
          values?.description
          || clinicalCare?.description
          || 'Triagem realizada pelo parceiro',
        outsourcedTransport: Boolean(values?.outsourcedTransport),
        clinicId: values?.clinic?.id,
        address,
      }
      const body = { eventData }
      const { data } = await clinicalCares.screening(id, body)
      dispatch(setClinicalCare({ ...data, tutor: data?.animal?.tutor }))
      toast.success('Triagem realizada com sucesso')
      setLoading(false)
      history.replace(`/app/clinical-cares/status/${data?.status || 'all'}/${data.id}`)
    } catch (e) {
      setLoading(false)
      toast.error(e?.message)
    }
  }

  return (
    <fieldset disabled={loading}>
      <div className="modal active" id="modal-id">
        <a
          type="button"
          className="modal-overlay"
          aria-label="Close"
          onClick={() => history.goBack()}
        />
        <div className="modal-container">
          <div className="modal-header">
            <a
              type="button"
              className="btn btn-clear float-right"
              onClick={() => history.goBack()}
            />
            <div className="modal-title h5">
              {`Fazer triagem do atendimento #${id}`}
            </div>
          </div>
          <div className="modal-body">
            <div className="content">
              <LoadManager loading={!clinicalCare}>
                <>
                  <div className="mb-2">
                    Revise os dados enviados na solicitação e complete as
                    informações necessárias para concluir a triagem e criar o atendimento.
                  </div>
                  <div className="text-small mb-1">
                    Nesta etapa você pode escolher a clínica para qual o pet será encaminhado,
                    a data e hora do agendamento e se o transporte será terceirizado ou não.
                  </div>
                  <div className="divider" />
                  <Form.Formik
                    initialValues={{
                      clinic:
                        clinicalCare?.clinicId || clinicalCare?.clinic?.id
                          ? {
                              id:
                                clinicalCare?.clinicId
                                || clinicalCare?.clinic?.id,
                            }
                          : null,
                      scheduledTo: clinicalCare?.scheduledTo
                        ? new Date(clinicalCare.scheduledTo)
                        : null,
                      description: clinicalCare?.description || '',
                      outsourcedTransport: Boolean(
                        clinicalCare?.outsourcedTransport,
                      ),
                    }}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                    validateOnMount
                  >
                    {({
                      touched, errors, isValid, values,
                    }) => (
                      <Form.ValidationForm>
                        <Flexbox.Columns>
                          <Flexbox.Column className="col-12">
                            {fetchingLicensings ? (
                              <div className="columns mx-1 mb-2">
                                <div className="column col-auto loading" />
                                <div className="column">
                                  <cite>Buscando clínicas disponíveis</cite>
                                </div>
                              </div>
                            ) : (
                              <Form.Group
                                className={`${
                                  touched?.clinic?.id && errors?.clinic?.id
                                    ? 'has-error'
                                    : ''
                                }`}
                              >
                                <Form.Label>
                                  Qual clínica realizará o atendimento?{' '}
                                  <span className="text-error">*</span>
                                </Form.Label>
                                {!licensings.length && (
                                  <ErrorBox
                                    error={{
                                      icon: 'fas fa-clinic-medical fa-3x text-error',
                                      title: 'Nenhuma clínica disponível',
                                      message:
                                        'Nenhuma clínica está licenciada para receber este encaminhamento.',
                                    }}
                                  >
                                    <Link
                                      className="btn btn-link"
                                      to="/app/clinical-cares/clinics"
                                    >
                                      <i className="fas fa-plus" />
                                      &nbsp;Gerenciar licenças de clínicas
                                    </Link>
                                  </ErrorBox>
                                )}
                                {licensings.map((licensing) => (
                                  <React.Fragment key={licensing?.id}>
                                    <small>
                                      <Form.Label className="form-radio mb-2">
                                        <Form.ValidationField
                                          type="radio"
                                          name="clinic.id"
                                          value={licensing?.licensedId}
                                          checked={
                                            String(values?.clinic?.id)
                                            === String(licensing?.licensedId)
                                          }
                                        />
                                        <i className="form-icon" />
                                        &nbsp;{licensing?.licensed?.fullName}
                                      </Form.Label>
                                    </small>
                                  </React.Fragment>
                                ))}
                                {touched?.clinic?.id && errors?.clinic?.id ? (
                                  <Form.Hint className="text-error">
                                    Selecione uma clínica para encaminhar.
                                  </Form.Hint>
                                ) : null}
                              </Form.Group>
                            )}
                          </Flexbox.Column>
                          <div className="column col-12 divider" />
                          <Flexbox.Column className="col-12">
                            <Form.Group
                              className={`${
                                touched?.scheduledTo && errors?.scheduledTo
                                  ? 'has-error'
                                  : ''
                              }`}
                            >
                              <Form.Label htmlFor="scheduledTo">
                                Agendado para{' '}
                                <span className="text-error">*</span>
                              </Form.Label>
                              <Form.ValidationField
                                name="scheduledTo"
                                id="scheduledTo"
                              >
                                {({ field }) => (
                                  <Form.DatePickerField
                                    name="scheduledTo"
                                    dateFormat="dd/MM/yyyy HH:mm"
                                    minDate={new Date()}
                                    showTimeSelect
                                    className="form-input"
                                    id="scheduledTo"
                                    placeholderText="Data e hora"
                                    selected={field.value || null}
                                  />
                                )}
                              </Form.ValidationField>
                              {touched?.scheduledTo && errors?.scheduledTo ? (
                                <Form.Hint className="text-error">
                                  Informe a data e hora de agendamento.
                                </Form.Hint>
                              ) : null}
                            </Form.Group>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-12 pt-2 mt-2">
                            <Form.Group>
                              <Form.Switch className="form-switch">
                                <Form.ValidationField
                                  type="checkbox"
                                  name="outsourcedTransport"
                                  id="outsourcedTransport"
                                />
                                <i className="form-icon" />
                                &nbsp;O Transporte será terceirizado
                              </Form.Switch>
                              <div style={{ marginTop: -8 }}>
                                <small className="text-gray">
                                  Se ativo, significa que o pet foi transportado
                                  por algum terceirizado
                                </small>
                              </div>
                            </Form.Group>
                          </Flexbox.Column>
                        </Flexbox.Columns>
                        <div
                          className="modal-footer"
                          style={{ paddingLeft: 0, paddingRight: 0 }}
                        >
                          <a
                            className="btn mr-2"
                            disabled={loading}
                            onClick={() => history.goBack()}
                          >
                            <i className="fas fa-chevron-left" />
                            &nbsp;Voltar
                          </a>
                          <button
                            className={`btn btn-success ${
                              loading ? 'loading' : ''
                            }`}
                            disabled={!isValid || loading}
                            type="submit"
                          >
                            <i className="fas fa-check" />
                            &nbsp;Confirmar triagem e criar atendimento
                          </button>
                        </div>
                      </Form.ValidationForm>
                    )}
                  </Form.Formik>
                </>
              </LoadManager>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  )
}

Triage.propTypes = {
  title: PropTypes.string.isRequired,
}

export default Triage
